/**
 * Admin → one group (container): name, permissions, scope and members, live
 * and dirty-aware (D35, `useLiveResource` — the `StudioView` shape): another
 * admin's save refreshes the page in place while it has no unsaved edits;
 * with unsaved edits the banner offers "Overwrite with mine" or "Discard mine
 * and load theirs", as does a save that answers 409 (`ifUpdatedAt`). A group
 * has no version number, so its `updatedAt` serves as one. Members change at
 * once, apart from the draft.
 */
import { knownPermissions, type PermissionIdWire } from '@norna/api-contract/permissions.js';
import type { GroupWire } from '@norna/api-contract/tenancy.js';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { GroupConflict } from '../../api/admin.js';
import { useAdminGroup, useAdminSites, useAdminUsers, useDeleteGroup, useGrants, useGroupMember, useLiveResource, useSaveGroup, useServices, useStudios } from '../../app/hooks.js';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog.js';
import { type GroupDraft, GroupEditor } from '../../components/admin/GroupEditor.js';
import { LiveUpdateBanner } from '../../components/LiveUpdateBanner.js';
import { grantablePermissions, lockedPermissions } from '../../lib/admin/permissions.js';
import { whenText } from '../../lib/live/whenText.js';
import { errorText, messageOf } from './common.js';

/** The draft edits the permissions this page knows; one from a newer release (`unknown`, D37) is kept by the server. */
function draftOf(group: GroupWire): GroupDraft {
  return { name: group.name, permissions: knownPermissions(group.permissions), scope: group.scope };
}

/** The group holds a permission a newer release granted, which this page can neither show nor send back. */
function holdsUnknown(group: GroupWire): boolean {
  return group.permissions.includes('unknown');
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((x) => b.includes(x));
}

function isDirty(draft: GroupDraft, base: GroupWire): boolean {
  return draft.name.trim() !== base.name || !sameSet(draft.permissions, knownPermissions(base.permissions)) || JSON.stringify(draft.scope) !== JSON.stringify(base.scope);
}

/** The draft's scope as sent: sorted ids, so an unchanged selection in another order is not a change. */
function scopeOf(scope: GroupWire['scope']): GroupWire['scope'] {
  return scope.kind === 'tenant' ? scope : { kind: 'resources', siteIds: [...scope.siteIds].sort(), studioIds: [...scope.studioIds].sort() };
}

function GroupPage({ latest, memberIds }: { readonly latest: GroupWire; readonly memberIds: readonly string[] }) {
  const { nowMs } = useServices();
  const grants = useGrants();
  const users = useAdminUsers();
  const sites = useAdminSites();
  const studios = useStudios();
  const save = useSaveGroup();
  const member = useGroupMember();
  const remove = useDeleteGroup();
  const navigate = useNavigate();
  const [loaded] = useState(latest);
  const [draft, setDraft] = useState<GroupDraft>(() => draftOf(loaded));
  const [deleting, setDeleting] = useState(false);
  const live = useLiveResource<GroupWire>({
    kind: 'group',
    id: loaded.groupId,
    loaded,
    latest,
    isDirty: (base) => isDirty(draft, base),
    versionOf: (g) => Date.parse(g.updatedAt),
    authorOf: () => 'another admin',
    writtenAtOf: (g) => Date.parse(g.updatedAt),
    onReset: (g) => setDraft(draftOf(g)),
  });
  const base = live.base;
  const locked = lockedPermissions(base.groupId);
  const isVendorTenant = grants?.some((g) => g.permissions.some((p) => p.startsWith('vendor.'))) === true;

  const submit = (ifUpdatedAt: string): void => {
    const permissions: PermissionIdWire[] = [...new Set([...draft.permissions, ...(locked ?? [])])];
    // Permissions unchanged: not sent, so the group keeps every one it holds — also one from a newer release. Changed
    // while it holds such a one: refused here, because the list sent would erase it.
    const changed = !sameSet(permissions, knownPermissions(base.permissions));
    if (changed && holdsUnknown(base)) {
      toast.error('Not saved: this group holds a permission from a newer release that this page does not know. Reload the page to change its permissions.');
      return;
    }
    live.beginSave();
    save.mutate(
      { groupId: base.groupId, ifUpdatedAt, name: draft.name.trim(), permissions: changed ? permissions : null, scope: scopeOf(draft.scope) },
      {
        onSuccess: (saved) => {
          live.saved(saved);
          toast.success(`Saved ${saved.name}`);
        },
        onError: (err) => {
          if (err instanceof GroupConflict) live.conflict(err.current.group);
          else {
            live.saveFailed();
            toast.error(`Not saved: ${messageOf(err)}`);
          }
        },
      },
    );
  };

  const all = users.data ?? [];
  const members = all.filter((u) => memberIds.includes(u.userId)).map((u) => ({ userId: u.userId, email: u.email }));
  const candidates = all.filter((u) => !memberIds.includes(u.userId)).map((u) => ({ userId: u.userId, email: u.email }));
  const notice = live.notice;
  const changeMember = (userId: string, isMember: boolean) =>
    member.mutate({ groupId: base.groupId, userId, member: isMember }, { onError: (err) => toast.error(messageOf(err)) });

  return (
    <div className="grid gap-4">
      {notice === null ? null : (
        <LiveUpdateBanner
          notice={{ kind: notice.kind, who: notice.by, at: whenText(notice.atMs, nowMs()), what: 'group' }}
          pending={save.isPending}
          canSave
          onOverwrite={() => {
            const theirs = live.keepMine();
            if (theirs !== null) submit(theirs.updatedAt);
          }}
          onDiscard={() => live.discardMine()}
        />
      )}
      <GroupEditor
        groupId={base.groupId}
        draft={draft}
        permissions={grantablePermissions(isVendorTenant)}
        locked={locked ?? []}
        protectedGroup={locked !== null}
        sites={(sites.data ?? []).filter((s) => s.kind === 'onprem').map((s) => ({ id: s.siteId, name: s.name }))}
        studios={(studios.data ?? []).map((s) => ({ id: s.studioId, name: s.name }))}
        members={members}
        candidates={candidates}
        dirty={isDirty(draft, base)}
        saving={save.isPending}
        memberPending={member.isPending}
        refreshes={live.refreshes}
        onDraftChange={setDraft}
        onSave={() => submit(base.updatedAt)}
        onReset={() => setDraft(draftOf(base))}
        onDelete={() => {
          remove.reset();
          setDeleting(true);
        }}
        onAddMember={(userId) => changeMember(userId, true)}
        onRemoveMember={(userId) => changeMember(userId, false)}
      />
      <ConfirmDialog
        open={deleting}
        title={`Delete ${base.name}?`}
        description="Its members lose what it granted, on every studio server at the next sync."
        confirmLabel="Delete group"
        typeToConfirm={null}
        pending={remove.isPending}
        error={errorText(remove.error)}
        onConfirm={() =>
          remove.mutate(
            { groupId: base.groupId },
            {
              onSuccess: () => {
                toast.success(`Deleted ${base.name}`);
                void navigate({ to: '/admin/groups' });
              },
            },
          )
        }
        onOpenChange={setDeleting}
      />
    </div>
  );
}

export function GroupAdminView({ groupId }: { readonly groupId: string }) {
  const group = useAdminGroup(groupId);
  if (group.data !== undefined) return <GroupPage latest={group.data.group} memberIds={group.data.memberIds} />;
  if (group.error !== null) return <p role="alert" className="p-8 text-center text-sm text-destructive">Could not load the group: {messageOf(group.error)}</p>;
  return <p className="p-8 text-center text-sm text-muted-foreground">Loading the group…</p>;
}
